/**
 * Pass 2 Schema — Sprint 16b
 *
 * Zod validation for the entity + opportunity analyzer LLM output.
 * Handles partial recovery: individual entities validated separately.
 */
import { z } from 'zod';
export const Pass2SignalSchema = z.object({
    source: z.enum(['financial', 'calendar', 'email', 'browsing']),
    proxy: z.enum(['attention', 'investment', 'communication', 'repetition', 'initiative', 'priority']),
    summary: z.string(),
    strength: z.number().min(0).max(1),
});
export const Pass2EntitySchema = z.object({
    name: z.string().min(1).max(100),
    type: z.enum(['thing', 'experience', 'person']),
    nature: z.string().max(200).optional(),
    category: z.string().min(1),
    subcategories: z.array(z.string()).default([]),
    confidence: z.number().min(0).max(1),
    stability: z.number().min(0).max(1).default(0.5),
    reasoning: z.string().min(10),
    ikigaiRelevance: z.object({
        love: z.number().min(0).max(1),
        goodAt: z.number().min(0).max(1).default(0),
        worldNeeds: z.number().min(0).max(1).default(0),
        paidFor: z.number().min(0).max(1).default(0),
    }),
    proxies: z.record(z.string()).default({}),
    signals: z.array(Pass2SignalSchema).min(1),
    actionableContext: z.object({
        location: z.string().max(200).optional(),
        budget: z.string().max(200).optional(),
        socialContext: z.string().max(200).optional(),
        timing: z.string().max(200).optional(),
        frequency: z.string().max(200).optional(),
        constraints: z.string().max(200).optional(),
        suggestedActions: z.array(z.string()).default([]),
    }).default({}),
    temporalPattern: z.string().max(200).optional(),
    deadline: z.string().optional(),
    urgencyReason: z.string().max(200).optional(),
    status: z.enum(['new', 'confirmed', 'updated', 'merged', 'declining']).default('new'),
});
export const Pass2OpportunitySchema = z.object({
    entityName: z.string().min(1),
    description: z.string().min(1),
    deadline: z.string().optional(),
    urgencyReason: z.string().max(200).optional(),
    suggestedMission: z.string().min(1),
    agentType: z.enum(['restaurant', 'travel', 'content', 'shopping', 'events', 'financial']),
    context: z.record(z.string()).default({}),
});
export const Pass2ResponseSchema = z.object({
    entities: z.array(Pass2EntitySchema),
    opportunities: z.array(Pass2OpportunitySchema).default([]),
});
/**
 * Extract JSON from an LLM response that may contain markdown code blocks.
 */
export function extractJSON(raw) {
    // Try to extract from markdown code block
    const codeBlockMatch = raw.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
    if (codeBlockMatch) {
        return codeBlockMatch[1].trim();
    }
    // Try to find JSON object directly
    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
        return jsonMatch[0];
    }
    return raw.trim();
}
/**
 * Attempt to repair truncated JSON by closing open brackets/braces.
 * This handles the common case where LLM output is cut off mid-response
 * due to max_tokens limits.
 */
function repairTruncatedJSON(jsonStr) {
    // Try parsing as-is first
    try {
        JSON.parse(jsonStr);
        return jsonStr;
    }
    catch { /* needs repair */ }
    // Remove any trailing incomplete string (cut off mid-value)
    let repaired = jsonStr;
    // If we're in the middle of a string value, close it
    // Count unescaped quotes to see if we have an open string
    let inString = false;
    let lastQuoteIdx = -1;
    for (let i = 0; i < repaired.length; i++) {
        if (repaired[i] === '\\') {
            i++;
            continue;
        } // skip escaped chars
        if (repaired[i] === '"') {
            inString = !inString;
            lastQuoteIdx = i;
        }
    }
    if (inString && lastQuoteIdx >= 0) {
        // We're inside an unclosed string — truncate to last complete value
        // Find the last complete JSON object boundary before the unclosed string
        repaired = repaired.substring(0, lastQuoteIdx) + '"';
    }
    // Remove trailing comma or colon (incomplete key-value pair)
    repaired = repaired.replace(/,\s*$/, '');
    repaired = repaired.replace(/:\s*$/, ': null');
    // Remove incomplete trailing key-value pairs (e.g., `"key": "incompl`)
    // by finding the last complete array element boundary
    // Look for the pattern where we have a partial object in an array
    const lastCompleteObjEnd = repaired.lastIndexOf('},');
    const lastObjClose = repaired.lastIndexOf('}');
    // Count open brackets to know what needs closing
    const openBrackets = [];
    inString = false;
    for (let i = 0; i < repaired.length; i++) {
        if (repaired[i] === '\\') {
            i++;
            continue;
        }
        if (repaired[i] === '"') {
            inString = !inString;
            continue;
        }
        if (inString)
            continue;
        if (repaired[i] === '{' || repaired[i] === '[')
            openBrackets.push(repaired[i]);
        if (repaired[i] === '}') {
            if (openBrackets[openBrackets.length - 1] === '{')
                openBrackets.pop();
        }
        if (repaired[i] === ']') {
            if (openBrackets[openBrackets.length - 1] === '[')
                openBrackets.pop();
        }
    }
    // If we have unclosed structures, try to truncate to last complete entity
    if (openBrackets.length > 2 && lastCompleteObjEnd > 0) {
        // Truncate to last complete object in the entities array, then close everything
        repaired = repaired.substring(0, lastCompleteObjEnd + 1);
        // Recount what needs closing
        openBrackets.length = 0;
        inString = false;
        for (let i = 0; i < repaired.length; i++) {
            if (repaired[i] === '\\') {
                i++;
                continue;
            }
            if (repaired[i] === '"') {
                inString = !inString;
                continue;
            }
            if (inString)
                continue;
            if (repaired[i] === '{' || repaired[i] === '[')
                openBrackets.push(repaired[i]);
            if (repaired[i] === '}') {
                if (openBrackets[openBrackets.length - 1] === '{')
                    openBrackets.pop();
            }
            if (repaired[i] === ']') {
                if (openBrackets[openBrackets.length - 1] === '[')
                    openBrackets.pop();
            }
        }
    }
    // Close remaining open brackets in reverse order
    for (let i = openBrackets.length - 1; i >= 0; i--) {
        repaired += openBrackets[i] === '{' ? '}' : ']';
    }
    return repaired;
}
/**
 * Parse and validate Pass 2 LLM output.
 *
 * Attempts full validation first, then partial recovery if needed.
 * Returns validated entities and any validation errors.
 */
export function parseAndValidatePass2(raw) {
    const errors = [];
    // Step 1: Extract JSON
    let jsonStr;
    try {
        jsonStr = extractJSON(raw);
    }
    catch (e) {
        return { entities: [], opportunities: [], errors: [`Failed to extract JSON: ${e}`], partialRecovery: false };
    }
    // Step 2: Parse JSON — with truncation repair fallback
    let parsed;
    try {
        parsed = JSON.parse(jsonStr);
    }
    catch (e) {
        // Try to repair truncated JSON (common when LLM hits max_tokens limit)
        try {
            const repaired = repairTruncatedJSON(jsonStr);
            parsed = JSON.parse(repaired);
            errors.push(`JSON was truncated and repaired — some entities may be missing`);
        }
        catch {
            return { entities: [], opportunities: [], errors: [`Malformed JSON: ${e}`], partialRecovery: false };
        }
    }
    // Step 3: Full validation
    const fullResult = Pass2ResponseSchema.safeParse(parsed);
    if (fullResult.success) {
        for (const entity of fullResult.data.entities) {
            if (warnIABStyleName(entity.name)) {
                console.warn(`[pass2-schema] IAB-style entity name detected: "${entity.name}" — consider natural naming`);
            }
        }
        return {
            entities: fullResult.data.entities,
            opportunities: fullResult.data.opportunities,
            errors: [],
            partialRecovery: false
        };
    }
    // Step 4: Partial recovery — validate each entity individually
    const obj = parsed;
    const rawEntities = Array.isArray(obj.entities) ? obj.entities : [];
    const rawOpportunities = Array.isArray(obj.opportunities) ? obj.opportunities : [];
    const validEntities = [];
    for (let i = 0; i < rawEntities.length; i++) {
        const entityResult = Pass2EntitySchema.safeParse(rawEntities[i]);
        if (entityResult.success) {
            validEntities.push(entityResult.data);
        }
        else {
            errors.push(`Entity ${i} invalid: ${entityResult.error.issues.map(iss => iss.message).join(', ')}`);
        }
    }
    const validOpportunities = [];
    for (const opp of rawOpportunities) {
        const oppResult = Pass2OpportunitySchema.safeParse(opp);
        if (oppResult.success) {
            validOpportunities.push(oppResult.data);
        }
    }
    if (validEntities.length === 0) {
        errors.unshift(`Full validation failed: ${fullResult.error.issues.map(iss => `${iss.path.join('.')}: ${iss.message}`).join('; ')}`);
    }
    return {
        entities: validEntities,
        opportunities: validOpportunities,
        errors,
        partialRecovery: validEntities.length > 0,
    };
}
/** Detect IAB taxonomy-style names: "Noun Phrase & Noun Phrase" pattern (length > 30 + contains ' & ') */
export function warnIABStyleName(name) {
    return name.length > 30 && name.includes(' & ');
}
//# sourceMappingURL=pass2-schema.js.map